import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Mail } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import ispaiLogo from '@/assets/ispai-logo-mark.png';

interface PitchDeckRequestFormProps {
  onClose?: () => void;
}

const PitchDeckRequestForm = ({ onClose }: PitchDeckRequestFormProps) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim() || !email.trim()) {
      toast({
        title: 'Missing Information',
        description: 'Please provide your name and email.',
        variant: 'destructive',
      });
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast({
        title: 'Invalid Email',
        description: 'Please enter a valid email address.',
        variant: 'destructive',
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const { data, error } = await supabase.functions.invoke('request-pitch-deck', {
        body: {
          name: name.trim(),
          email: email.trim().toLowerCase(),
          company: company.trim(),
          message: message.trim(),
        },
      });

      if (error || !data?.success) {
        throw new Error(error?.message || 'Request failed');
      }

      setSubmitted(true);
      toast({
        title: 'Request Sent',
        description: 'Thanks — we\'ll be in touch shortly.',
      });
    } catch (err) {
      console.error('Pitch deck request failed:', err);
      toast({
        title: 'Something went wrong',
        description: 'Please try again in a moment.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="flex flex-col items-center justify-center p-8 bg-gradient-to-b from-gray-900 to-black text-white">
        <div className="w-full max-w-md text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 mb-4">
            <img src={ispaiLogo} alt="iSPAI" className="w-16 h-16 object-contain" />
          </div>
          <h2 className="text-2xl font-bold mb-3">Request Received</h2>
          <p className="text-gray-300 mb-6">
            Thank you for your interest in iSPAI. The team will review your request and
            follow up with the pitch deck shortly.
          </p>
          {onClose && (
            <Button onClick={onClose} className="bg-white text-black hover:bg-gray-200">
              Close
            </Button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center p-8 bg-gradient-to-b from-gray-900 to-black text-white">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 mb-4">
            <img src={ispaiLogo} alt="iSPAI" className="w-16 h-16 object-contain" />
          </div>
          <h2 className="text-2xl font-bold mb-2">Request the iSPAI Pitch Deck</h2>
          <p className="text-gray-400">
            Share a few details and we'll send the deck directly to you.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="pd-name" className="text-gray-300">Full Name</Label>
            <Input
              id="pd-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Jane Doe"
              maxLength={100}
              className="bg-white/10 border-white/20 text-white placeholder:text-gray-500"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="pd-email" className="text-gray-300">Email Address</Label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                id="pd-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                maxLength={255}
                className="pl-10 bg-white/10 border-white/20 text-white placeholder:text-gray-500"
                required
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="pd-company" className="text-gray-300">Company / Firm (optional)</Label>
            <Input
              id="pd-company"
              value={company}
              onChange={(e) => setCompany(e.target.value)}
              placeholder="Acme Ventures"
              maxLength={150}
              className="bg-white/10 border-white/20 text-white placeholder:text-gray-500"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="pd-message" className="text-gray-300">Message (optional)</Label>
            <Textarea
              id="pd-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Anything you'd like us to know…"
              maxLength={2000}
              rows={3}
              className="bg-white/10 border-white/20 text-white placeholder:text-gray-500"
            />
          </div>

          <Button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-white text-black hover:bg-gray-200"
          >
            {isSubmitting ? 'Sending…' : 'Request Pitch Deck'}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default PitchDeckRequestForm;
